import * as React from 'react'
import { Heading } from '../../Text'
import { Button } from '../../Button'
import { Form } from '../Form'
import { Field } from '../Fields/Field'
import { PortableText } from '@portabletext/react'
import Link from 'next/link'
import * as Yup from 'yup'
import { useUser } from '../../../providers/UserProvider'
import { useRouter } from 'next/router'
import { Sentry } from '../../../services/sentry'

import {
  SignupWrapper,
  ConsentWrapper,
  FieldsWrapper as BaseFieldsWrapper,
  SignupFieldsWrapper,
  InputWrapper,
} from './styled'
import { getCookie, setCookie } from '../../../utils'
import { RsvpPage as RsvpPageType } from '../../../types/generated-sanity'
import { SignupFormProps } from '../../../types/modal'
import { styled } from '@xstyled/styled-components'
import { generateEmailUUID } from '../../../providers/UserProvider/utils'
import { useAnalytics } from '../../../providers/AnalyticsProvider'
import { SeverityLevel } from '@sentry/types'

const { useState, useEffect } = React

interface RsvpPageFormProps extends SignupFormProps {
  page: RsvpPageType
  colorTheme?: 'light' | 'dark'
}

interface GeoLocationData {
  city?: string
  country?: string
  flag?: string
  region?: string
  countryRegion?: string
  latitude?: string
  longitude?: string
}

type FormValues = {
  email?: string
  phone?: string
  phoneCountryCode?: string
  dialingCode?: string
  smsConsent: boolean
  emailConsent: boolean
}

const VipOptOutLabel = styled.span`
  font-style: italic;
  color: red;
  display: inline;
  font-weight: 300;
`

const RsvpInputWrapper = styled(InputWrapper)`
  .field {
    width: 100%;
  }
  .field input {
    background-color: transparent !important;
  }
`

const BrazeOptionInputWrapper = styled(InputWrapper)<{ $visible: boolean }>`
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  .field {
    width: 100%;
    input,
    select {
      background-color: transparent !important;
      border-color: grays.8 !important;
      color: grays.8 !important;
    }
  }
`

export const RsvpPageForm = ({
  onContinue,
  settings,
  page,
  colorTheme,
}: RsvpPageFormProps) => {
  const { changeUser, getUser, openSession, logCustomEvent, geoLocation } =
    useUser()
  const { sendFormSubmit } = useAnalytics()

  const {
    title,
    slug,
    heading,
    subheadingRaw,
    footerText,
    links,
    heading_submitted,
    subheading_submitted_vip,
    subheading_submitted_email,
    subheading_submitted_sms,
    submittedButtonText,
    submittedButtonLink,
    brazeOptions,
    disableEmailSignup,
    disablePhoneSignup,
  } = page

  const router = useRouter()
  const { pathname, asPath } = router

  // Determine initial consent values based on disable flags
  const initialEmailConsent = disableEmailSignup
    ? false
    : disablePhoneSignup
    ? true
    : true
  const initialSmsConsent = disablePhoneSignup
    ? false
    : disableEmailSignup
    ? true
    : true

  // Add initial values for BrazeOptionGroup
  const brazeGroupInitials = Array.isArray(page.brazeOptions)
    ? page.brazeOptions.reduce((acc, option) => {
        if (option && option._type === 'brazeOptionGroup') {
          const group = option as { options: { key: string; value: string }[] }
          if (
            Array.isArray(group.options) &&
            group.options.length > 0 &&
            typeof group.options[0].key === 'string'
          ) {
            const groupKey = group.options[0].key
            const groupValue = group.options[0].value
            if (groupKey && groupValue) {
              acc[`braze-${groupKey}`] = groupValue
            }
          }
        }
        return acc
      }, {} as Record<string, string>)
    : {}

  // Add initial values for BrazeOptionHidden
  const brazeHiddenInitials = Array.isArray(page.brazeOptions)
    ? page.brazeOptions.reduce((acc, option) => {
        if (option && option._type === 'brazeOptionHidden') {
          const hidden = option as { key?: string; value?: string }
          if (hidden.key && hidden.value) {
            acc[`braze-${hidden.key}`] = hidden.value
          }
        }
        return acc
      }, {} as Record<string, string>)
    : {}

  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [formState, setFormState] = useState<FormValues>({
    smsConsent: initialSmsConsent,
    emailConsent: initialEmailConsent,
    email: '',
    phone: '',
    phoneCountryCode: 'US-United States',
    dialingCode: '1',
    ...brazeGroupInitials,
    ...brazeHiddenInitials,
  })
  const [vipOptOutEmail, setVipOptOutEmail] = useState(false)
  const [vipOptOutSms, setVipOptOutSms] = useState(false)
  const [submitText, setSubmitText] = useState('RSVP')
  const [submittedSubheading, setSubmittedSubheading] = useState(
    'Please confirm your subscription.',
  )
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (values: FormValues) => {
    setSubmitting(true)
    setError(null)

    const user = getUser()

    console.log('handleSubmit values:', values)

    try {
      const strippedPhoneNumber = values.phone?.replace(/[^0-9]/g, '')
      const formattedPhoneNumber = `+${values.dialingCode}${strippedPhoneNumber}`

      // Add breadcrumb for debugging
      Sentry.addBreadcrumb({
        category: 'form',
        message: 'User attempted RSVP form submission (landing page)',
        level: 'info' as SeverityLevel,
        data: {
          email: values.email,
          phone: values.phone,
          emailConsent: values.emailConsent,
          smsConsent: values.smsConsent,
          phoneCountryCode: values.phoneCountryCode,
          dialingCode: values.dialingCode,
          ...Object.fromEntries(
            Object.entries(values).filter(([key]) => key.startsWith('braze-')),
          ),
        },
      })

      if (!values.emailConsent && values.smsConsent && values.phone) {
        // If no email consent, we are only sending a phone number to Braze
        const userPhoneNumberSet = user.setPhoneNumber(formattedPhoneNumber)
        // add user to SMS subscription group
        const userSubscribed = user.addToSubscriptionGroup(
          'fe589b39-05c1-45d9-8efe-a9bd9e6a2f91',
        )
        console.log('user subscribed to sms group', userSubscribed)

        setSuccess(true)
        setSubmitting(false)
        return
      } else if (values.email && values.emailConsent) {
        const emailUUID = await generateEmailUUID(values.email)

        await changeUser(emailUUID)

        const userSubscribedEmail = user.addToSubscriptionGroup(
          'dd1fcdc9-b13d-4c5a-8f5f-252a2da00bd9',
        )
        console.log('user subscribed to email group', userSubscribedEmail)

        if (values.phone && values.smsConsent) {
          const userPhoneNumberSet = user.setPhoneNumber(formattedPhoneNumber)
          // add user to SMS subscription group
          const userSubscribed = user.addToSubscriptionGroup(
            'fe589b39-05c1-45d9-8efe-a9bd9e6a2f91',
          )
          console.log('user subscribed to sms group', userSubscribed)
        }

        openSession()

        await fetch('/api/hubspotUserSignup', {
          method: 'POST',
          body: JSON.stringify(values),
        }).then((r) => {
          return r.json()
        })
        setSuccess(true)
        setSubmitting(false)
      }
    } catch (error) {
      setSubmitting(false)
      setError('Something went wrong. Please try again.')

      Sentry.captureException(error, 'signup_form_error', {
        formValues: values,
        pathname,
        asPath,
        type: 'rsvp_page',
        country: getCookie('VIEWER_COUNTRY'),
        subscriptionType:
          formState.emailConsent && formState.smsConsent
            ? 'vip'
            : formState.emailConsent
            ? 'email'
            : 'sms',
        ...Object.fromEntries(
          Object.entries(values).filter(([key]) => key.startsWith('braze-')),
        ),
      })
    }
  }

  const initialValues = {
    email: '',
    phone: '',
    smsConsent: initialSmsConsent,
    emailConsent: initialEmailConsent,
    phoneCountryCode: 'US-United States',
    dialingCode: '',
    ...brazeGroupInitials,
    ...brazeHiddenInitials,
  }

  // Adjust validation schema based on disable flags
  const validationSchema = Yup.object()
    .shape({
      emailConsent: Yup.boolean(),
      smsConsent: Yup.boolean(),
      email: disablePhoneSignup
        ? Yup.string()
            .email('Invalid email address')
            .required('Email is required')
        : Yup.string().when('emailConsent', {
            is: true,
            then: (schema) =>
              schema
                .email('Invalid email address')
                .required('Email is required'),
            otherwise: (schema) => schema.notRequired(),
          }),
      phone: disableEmailSignup
        ? Yup.string().required('Phone number is required')
        : Yup.string().when(['smsConsent', 'email'], {
            is: (smsConsent: boolean, email: string) => smsConsent && !email,
            then: (schema) => schema.required('Phone number is required'),
            otherwise: (schema) => schema.notRequired(),
          }),
    })
    .test(
      'at-least-one-consent',
      'You must opt-in to at least one of email or SMS',
      (values) => values.emailConsent || values.smsConsent,
    )

  useEffect(() => {
    if (formState.smsConsent && !formState.emailConsent) {
      setSubmitText('RSVP')
      setSubmittedSubheading(
        subheading_submitted_sms ||
          'Please confirm your subscription by checking your text messages.',
      )
      setVipOptOutEmail(true)
    } else if (!formState.smsConsent && formState.emailConsent) {
      setSubmitText('RSVP')
      setSubmittedSubheading(
        subheading_submitted_email ||
          'Please confirm your subscription by checking your email.',
      )
      setVipOptOutSms(true)
    } else if (!formState.emailConsent && !formState.smsConsent) {
      setSubmitText('RSVP')
      setVipOptOutEmail(false)
      setVipOptOutSms(false)
    } else {
      setVipOptOutEmail(false)
      setVipOptOutSms(false)
      setSubmitText('RSVP')
      setSubmittedSubheading(
        subheading_submitted_vip ||
          'Please confirm your subscription by checking both your email inbox and text messages.',
      )
    }
  }, [
    formState,
    subheading_submitted_email,
    subheading_submitted_sms,
    subheading_submitted_vip,
  ])

  useEffect(() => {
    const logSignupEvent = () => {
      if (success) {
        sendFormSubmit(`rsvp-${slug?.current || ''}`)
        logCustomEvent('rsvp_page_complete', {
          id: `rsvp-${slug?.current || ''}`,
          path: asPath,
          country:
            geoLocation?.country || getCookie('VIEWER_COUNTRY') || undefined,
          city: geoLocation?.city,
          subscribe:
            formState.emailConsent && formState.smsConsent
              ? 'vip'
              : formState.emailConsent
              ? 'email'
              : 'sms',
          phoneRegion:
            formState.dialingCode === '1' ? 'domestic' : 'international',
          ...Object.fromEntries(
            Object.entries(formState)
              .filter(([key]) => key.startsWith('braze-'))
              .map(([key, value]) => [key.replace('braze-', ''), value]),
          ),
        })
        // Set cookie to prevent modal from opening automatically again
        setCookie('SK_SIGNUP_SUBMITTED', 'true', {
          expires: 365,
        })
      }
    }

    logSignupEvent()
  }, [success])

  const handleFormStateChange = (values: FormValues) => {
    setFormState(values)
    console.log('handleFormStateChange values:', values)
  }

  const handleContinueClick = () => {
    onContinue && onContinue()
  }

  return (
    <SignupWrapper $colorTheme={colorTheme || 'light'}>
      <Heading mt={0} mb={2} level={3}>
        {success
          ? heading_submitted || 'Thank you!'
          : heading || 'Join Our Circle'}
      </Heading>
      {!success && <PortableText value={subheadingRaw} />}
      {success && <Heading level={4}>{submittedSubheading}</Heading>}

      <Form
        id="signup-form"
        disabled={submitting}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onFormStateChange={handleFormStateChange}
      >
        <SignupFieldsWrapper
          $colorTheme={colorTheme || 'light'}
          $visible={!success}
        >
          {/* Conditionally render email fields */}
          {!disableEmailSignup && (
            <RsvpInputWrapper $colorTheme={colorTheme || 'light'}>
              <Field
                name="email"
                type="email"
                placeholder="Email Address"
                label="Email"
                disabled={!formState.emailConsent}
                required={
                  disablePhoneSignup ||
                  (formState.emailConsent && !formState.phone)
                }
              />
            </RsvpInputWrapper>
          )}
          {/* Conditionally render phone fields */}
          {!disablePhoneSignup && (
            <RsvpInputWrapper $colorTheme={colorTheme || 'light'}>
              <Field
                name="phone"
                type="tel"
                placeholder="Phone"
                label="Phone"
                disabled={!formState.smsConsent}
                required={
                  disableEmailSignup ||
                  (formState.smsConsent && !formState.email)
                }
              />
            </RsvpInputWrapper>
          )}
          {/* Render Braze options as dropdown selects or hidden fields */}
          {Array.isArray(brazeOptions) && brazeOptions.length > 0 && (
            <>
              {brazeOptions.map((option) => {
                // Type guards
                const isGroup = (
                  opt: any,
                ): opt is {
                  _type: string
                  _key?: string
                  title: string
                  options: any[]
                } =>
                  opt &&
                  opt._type === 'brazeOptionGroup' &&
                  typeof opt.title === 'string' &&
                  Array.isArray(opt.options) &&
                  opt.options.length > 0 &&
                  typeof opt.options[0]?.key === 'string'
                const isHidden = (
                  opt: any,
                ): opt is { _type: string; key: string; value: string } =>
                  opt &&
                  opt._type === 'BrazeOptionHidden' &&
                  typeof opt.key === 'string' &&
                  typeof opt.value === 'string'
                if (isGroup(option)) {
                  const fieldName = `braze-${option.options[0].key}`
                  return (
                    <BrazeOptionInputWrapper
                      key={option._key || fieldName}
                      $colorTheme={colorTheme || 'light'}
                      $visible={!success}
                    >
                      <Field
                        name={fieldName}
                        type="select"
                        label={option.title}
                        options={option.options
                          .filter(
                            (
                              opt,
                            ): opt is {
                              label: string
                              value: string
                              _key?: string
                            } =>
                              !!opt &&
                              typeof opt.label === 'string' &&
                              typeof opt.value === 'string',
                          )
                          .map((opt) => ({
                            value: opt.value,
                            label: opt.label,
                            id: opt._key || opt.value,
                          }))}
                        required
                      />
                    </BrazeOptionInputWrapper>
                  )
                }
                if (isHidden(option)) {
                  return (
                    <Field
                      key={option.key}
                      name={`braze-${option.key}`}
                      type="hidden"
                    />
                  )
                }
                return null
              })}
            </>
          )}
          {!success && (
            <Button
              type="submit"
              // Always enabled since at least one consent is true
              disabled={false}
            >
              {submitText}
            </Button>
          )}
          {success && (
            <Button
              onClick={handleContinueClick}
              type="button"
              mt={3}
              level={3}
            >
              {submittedButtonText}
            </Button>
          )}
          <ConsentWrapper>
            {footerText}
            <div className="links-wrapper">
              <Link href="/about/privacy-policy" target="_blank">
                Privacy Policy
              </Link>
              <Link href="/about/terms-and-conditions" target="_blank">
                Terms & Conditions
              </Link>
            </div>
          </ConsentWrapper>
        </SignupFieldsWrapper>
      </Form>
    </SignupWrapper>
  )
}
